import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  User,
  Heart,
  Pill,
  AlertTriangle,
  Loader2,
  Bed,
  Pencil,
  X,
  Check,
  Plus,
  Trash2,
  Paperclip,
  FileText,
  File,
  Image,
  Eye,
  Download,
  Upload,
  Activity,
  Ambulance,
  Bell,
  Droplets,
  HeartPulse,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import OssWithingsPressione from "../../components/OssWithingsPressione";
import MonitoraggioVitale from "../../components/MonitoraggioVitale";
import { useOssAuth } from "../../context/OssAuthContext";
import { toast } from "sonner";
import { openPrintPlaceholderWindow, printPdfBlob } from "../../utils/printPdfBlob";
import { canNativeSaveFile, saveOssBlob } from "../../lib/oss/bridge";
import { canNativePrint, printOssPdf } from "../../lib/oss/print";
import TerapieExtraSection from "../../components/fascicoli/TerapieExtraSection";
import TerapieInCorsoSection from "../../components/fascicoli/TerapieInCorsoSection";
import TagListEditor from "../../components/TagListEditor";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ReadField({ label, value, fallback = "N/D" }) {
  return (
    <div>
      <Label className="text-slate-500 text-xs">{label}</Label>
      <p className="font-medium text-slate-900 mt-0.5">{value?.trim() ? value : fallback}</p>
    </div>
  );
}

function buildAusiliListe(san) {
  return (san.ausili_utilizzati || []).filter((a) => String(a ?? "").trim());
}

function buildDiagnosiListe(san) {
  return {
    diagnosi_secondarie: (san.diagnosi_secondarie || []).filter((d) => String(d ?? "").trim()),
    patologie_croniche: (san.patologie_croniche || []).filter((p) => String(p ?? "").trim()),
    allergie: (san.allergie || []).filter((a) => String(a ?? "").trim()),
  };
}

function giorniAllaScadenzaDoc(scadenzaStr) {
  if (!scadenzaStr) return null;
  try {
    const scadenza = new Date(scadenzaStr);
    const oggi = new Date();
    oggi.setHours(0, 0, 0, 0);
    scadenza.setHours(0, 0, 0, 0);
    return Math.ceil((scadenza - oggi) / (1000 * 60 * 60 * 24));
  } catch (_) {
    return null;
  }
}

export default function OssFascicoloDettaglio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isSanitario = location.pathname.startsWith("/sanitario");
  const listPath = isSanitario ? "/sanitario/scheda" : "/oss/fascicoli";
  const fromCameraId = location.state?.fromCamera;
  const backPath =
    !isSanitario && fromCameraId ? `/oss/camere/${fromCameraId}` : listPath;
  const backLabel =
    !isSanitario && fromCameraId ? "Torna alla camera" : "Torna ai fascicoli";
  const { token, activeOss: oss } = useOssAuth();
  const [fascicolo, setFascicolo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdf118Generating, setPdf118Generating] = useState(false);
  const [activeTab, setActiveTab] = useState("pressione");
  const [alertTerapieInCorso, setAlertTerapieInCorso] = useState(0);
  const [alertTerapieExtra, setAlertTerapieExtra] = useState(0);

  // Edit states
  const [editDiagnosi, setEditDiagnosi] = useState(false);
  const [diagnosiForm, setDiagnosiForm] = useState(null);
  const [savingDiagnosi, setSavingDiagnosi] = useState(false);

  const [editTerapie, setEditTerapie] = useState(false);
  const [terapieForm, setTerapieForm] = useState(null);
  const [savingTerapie, setSavingTerapie] = useState(false);

  const [diagnosiEditKey, setDiagnosiEditKey] = useState(0);
  const [terapieEditKey, setTerapieEditKey] = useState(0);

  const diagnosiPrincipaleRef = useRef(null);
  const medicoCuranteRef = useRef(null);
  const aslRiferimentoRef = useRef(null);
  const medicoTelRef = useRef(null);
  const medicoEmailRef = useRef(null);
  const gruppoSanguignoRef = useRef(null);
  const dietaRef = useRef(null);
  const pianiTerapeuticiRef = useRef(null);
  const noteSanitarieRef = useRef(null);

  const terapieAusiliFileInputRef = useRef(null);
  const pianiTerapeuticiFileInputRef = useRef(null);
  const scadenzaPianiRef = useRef(null);
  const [uploadingTerapieAusili, setUploadingTerapieAusili] = useState(false);
  const [uploadingPianiTerapeutici, setUploadingPianiTerapeutici] = useState(false);

  const applyAlertCountsFromFascicolo = useCallback((data) => {
    if (!data) return;
    setAlertTerapieInCorso(Number(data.alert_terapie_in_corso) || 0);
    setAlertTerapieExtra(Number(data.alert_terapie_extra) || 0);
  }, []);

  const loadFascicolo = useCallback(() => {
    if (!token || !id) return;
    axios
      .get(`${API_URL}/api/oss/me/fascicoli/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => {
        setFascicolo(data);
        applyAlertCountsFromFascicolo(data);
      })
      .catch(() => setError("Fascicolo non trovato o accesso negato"))
      .finally(() => setLoading(false));
  }, [token, id, applyAlertCountsFromFascicolo]);

  const countPendingAlerts = (items = []) =>
    items.filter((slot) => !slot.somministrata && ["due", "overdue"].includes(slot.stato)).length;

  const loadAlertCounts = useCallback(() => {
    if (!token || !id) return;
    const headers = { Authorization: `Bearer ${token}` };
    axios
      .get(`${API_URL}/api/oss/me/fascicoli/${id}/terapie-in-corso/pending?scope=today`, { headers })
      .then(({ data }) => setAlertTerapieInCorso(countPendingAlerts(data?.items)))
      .catch(() => {});
    axios
      .get(`${API_URL}/api/oss/me/fascicoli/${id}/terapie-extra/alerts?scope=today`, { headers })
      .then(({ data }) => setAlertTerapieExtra(countPendingAlerts(data?.items)))
      .catch(() => {});
  }, [token, id]);

  useEffect(() => {
    loadFascicolo();
  }, [loadFascicolo]);

  useEffect(() => {
    const interval = setInterval(loadAlertCounts, 60000);
    return () => clearInterval(interval);
  }, [loadAlertCounts]);

  const handleSaveDiagnosi = async () => {
    if (!diagnosiForm) return;
    const payload = {
      diagnosi_principale: diagnosiPrincipaleRef.current?.value ?? "",
      diagnosi_secondarie: diagnosiForm.diagnosi_secondarie,
      patologie_croniche: diagnosiForm.patologie_croniche,
      medico_curante: medicoCuranteRef.current?.value ?? "",
      asl_riferimento: aslRiferimentoRef.current?.value ?? "",
      medico_curante_telefono: medicoTelRef.current?.value ?? "",
      medico_curante_email_personale: medicoEmailRef.current?.value ?? "",
      gruppo_sanguigno: gruppoSanguignoRef.current?.value ?? "",
      dieta: dietaRef.current?.value ?? "",
      allergie: diagnosiForm.allergie,
    };
    setSavingDiagnosi(true);
    try {
      await axios.patch(
        `${API_URL}/api/oss/me/fascicoli/${id}/sanitaria`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Diagnosi e patologie aggiornate");
      setEditDiagnosi(false);
      setFascicolo((f) => ({ ...f, sanitaria: { ...f.sanitaria, ...payload } }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingDiagnosi(false);
    }
  };

  const handleSaveTerapie = async () => {
    if (!terapieForm) return;
    const payload = {
      ausili_utilizzati: terapieForm,
      piani_terapeutici_testo: pianiTerapeuticiRef.current?.value ?? "",
      note_sanitarie: noteSanitarieRef.current?.value ?? "",
    };
    setSavingTerapie(true);
    try {
      await axios.patch(
        `${API_URL}/api/oss/me/fascicoli/${id}/sanitaria`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Ausili aggiornati");
      setEditTerapie(false);
      setFascicolo((f) => ({ ...f, sanitaria: { ...f.sanitaria, ...payload } }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSavingTerapie(false);
    }
  };

  const canDeleteDoc = (doc) =>
    Boolean(doc?.uploaded_by_oss_id && oss?.id && doc.uploaded_by_oss_id === oss.id);

  const handleViewDocument = (doc) => {
    // App nativa: i popup verso /api sono bloccati dal wrapper -> il file passa da OssApp.saveFile
    if (canNativeSaveFile()) {
      handleDownloadDocument(doc);
      return;
    }
    const url = `${API_URL}/api/upload/cartella/${id}/${doc.sezione}/${doc.file_id}?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/upload/cartella/${id}/${doc.sezione}/${doc.file_id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      const blob = new Blob([response.data], { type: doc.file_type || "application/octet-stream" });
      await saveOssBlob(blob, doc.file_name || `documento${doc.file_ext || ""}`);
    } catch (err) {
      console.error(err);
      toast.error("Errore nel download del documento");
    }
  };

  const handlePdf118Emergenza = async () => {
    if (!token || !id) return;
    const a = fascicolo?.anagrafica || {};
    const title = `118 — ${a.cognome || ""} ${a.nome || ""}`.trim() || "Scheda emergenza 118";
    if (canNativePrint("pdf") || canNativeSaveFile()) {
      // App nativa: niente popup di stampa nella WebView -> stampa di sistema (o, se il bridge
      // è di una versione precedente senza printPdf, il PDF viene aperto/condiviso)
      setPdf118Generating(true);
      try {
        const response = await axios.get(`${API_URL}/api/oss/me/fascicoli/${id}/118.pdf`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        });
        const slug = `${a.cognome || ""}-${a.nome || ""}`.trim().replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "");
        const printed = await printOssPdf(response.data, title);
        if (!printed) await saveOssBlob(response.data, `118-${slug || "ospite"}.pdf`);
        toast.success(printed ? "Scheda 118 inviata alla stampa" : "Scheda 118 pronta");
      } catch (err) {
        console.error(err);
        toast.error("Errore nella generazione della scheda 118");
      } finally {
        setPdf118Generating(false);
      }
      return;
    }
    const printWindow = openPrintPlaceholderWindow(title);
    if (!printWindow) {
      toast.error("Popup bloccato dal browser. Abilita i popup per stampare.");
      return;
    }
    setPdf118Generating(true);
    try {
      const response = await axios.get(`${API_URL}/api/oss/me/fascicoli/${id}/118.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const result = printPdfBlob(response.data, { title, printWindow });
      if (!result.ok) {
        printWindow.close();
        toast.error("Popup bloccato dal browser. Abilita i popup per stampare.");
        return;
      }
      toast.success("PDF pronto — dialogo di stampa aperto");
    } catch (err) {
      console.error(err);
      try {
        printWindow.close();
      } catch {
        /* ignore */
      }
      const maybeJson = err.response?.data instanceof Blob;
      let detailMsg;
      if (maybeJson) {
        try {
          const t = await err.response.data.text();
          detailMsg = JSON.parse(t)?.detail;
        } catch (_) {
          detailMsg = null;
        }
      } else {
        detailMsg = err.response?.data?.detail;
      }
      toast.error(detailMsg || "Impossibile generare il PDF. Riprova.");
    } finally {
      setPdf118Generating(false);
    }
  };

  const handleDeleteDocument = async (doc) => {
    if (!canDeleteDoc(doc)) return;
    if (!window.confirm("Eliminare questo documento?")) return;
    try {
      await axios.delete(`${API_URL}/api/oss/me/fascicoli/${id}/documenti/${doc.file_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Documento eliminato");
      loadFascicolo();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const handleUploadTerapieAusili = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File troppo grande. Massimo 50 MB");
      event.target.value = "";
      return;
    }
    const allowedTypes = [".pdf", ".jpg", ".jpeg", ".png", ".gif", ".doc", ".docx", ".xls", ".xlsx"];
    const ext = `.${file.name.split(".").pop().toLowerCase()}`;
    if (!allowedTypes.includes(ext)) {
      toast.error(`Tipo file non supportato. Formati ammessi: ${allowedTypes.join(", ")}`);
      event.target.value = "";
      return;
    }
    setUploadingTerapieAusili(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("descrizione", file.name || "Terapie e Ausili");
    try {
      await axios.post(`${API_URL}/api/oss/me/fascicoli/${id}/upload/terapie_ausili`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("File caricato");
      loadFascicolo();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploadingTerapieAusili(false);
      event.target.value = "";
    }
  };

  const handleUploadPianoTerapeutico = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File troppo grande. Massimo 50 MB");
      event.target.value = "";
      return;
    }
    const allowedTypes = [".pdf", ".jpg", ".jpeg", ".png", ".gif", ".doc", ".docx", ".xls", ".xlsx"];
    const ext = `.${file.name.split(".").pop().toLowerCase()}`;
    if (!allowedTypes.includes(ext)) {
      toast.error(`Tipo file non supportato. Formati ammessi: ${allowedTypes.join(", ")}`);
      event.target.value = "";
      return;
    }
    setUploadingPianiTerapeutici(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("descrizione", file.name || "Piano terapeutico");
    const scadenza = scadenzaPianiRef.current?.value?.trim();
    if (scadenza) formData.append("scadenza", scadenza);
    try {
      await axios.post(`${API_URL}/api/oss/me/fascicoli/${id}/upload/piani_terapeutici`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("File caricato");
      loadFascicolo();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploadingPianiTerapeutici(false);
      event.target.value = "";
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (error || !fascicolo) {
    return (
      <div className="text-center py-16 text-slate-500">
        <p>{error || "Fascicolo non trovato"}</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(backPath)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {backLabel}
        </Button>
      </div>
    );
  }

  const anag = fascicolo.anagrafica || {};
  const san = fascicolo.sanitaria || {};
  const cognome = (anag.cognome || "").trim();
  const nomeSolo = (anag.nome || "").trim();
  const nome = `${nomeSolo} ${cognome}`.trim() || "Ospite";
  const nomeDisplay = cognome
    ? `${cognome}${nomeSolo ? ` ${nomeSolo}` : ""}`
    : nome;

  const documenti = fascicolo.documenti || [];
  const docsTerapieAusili = documenti.filter((d) => d.sezione === "terapie_ausili");
  const docsPianiTerapeutici = documenti.filter((d) => d.sezione === "piani_terapeutici");

  const cameraLabel =
    fascicolo.camera && fascicolo.camera !== "N/A" ? fascicolo.camera : null;

  const tabTriggerClass =
    "group flex flex-row items-center justify-center gap-1.5 min-h-[48px] py-2 px-2 rounded-xl border-2 border-slate-200 bg-white text-slate-800 shadow-sm text-center whitespace-nowrap font-semibold text-xs sm:text-sm leading-none transition-all touch-manipulation hover:border-purple-300 hover:bg-purple-50/60 data-[state=active]:border-transparent data-[state=active]:bg-gradient-to-br data-[state=active]:from-purple-600 data-[state=active]:to-purple-800 data-[state=active]:text-white data-[state=active]:shadow-md";

  return (
    <div className="max-w-5xl mx-auto">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Identità + tab misurazioni in un unico sticky (tablet).
            In Sanità lo scroll è il <main> sotto l'header: top-16 sposterebbe
            la barra sopra il contenuto. In OSS l'header flottante è nello
            stesso scroll della pagina, quindi serve top-16. */}
        <div className={`sticky ${isSanitario ? "top-0 -mx-1 rounded-2xl border border-slate-200/90 bg-white shadow-sm" : "top-[5.25rem] oss-header-bar"} z-30 mb-4 overflow-hidden print:static print:shadow-none print:border-slate-300`}>
          <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(backPath)}
              className="shrink-0 h-11 w-11 touch-manipulation"
              aria-label={backLabel}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>

            {anag.foto_url ? (
              <img
                src={anag.foto_url}
                alt=""
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-purple-200 shrink-0"
              />
            ) : (
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-purple-100 flex items-center justify-center shrink-0 border border-purple-200">
                <User className="w-7 h-7 sm:w-8 sm:h-8 text-purple-500" />
              </div>
            )}

            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 leading-tight truncate">
                {nomeDisplay}
              </h1>
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                {cameraLabel ? (
                  <Badge
                    variant="outline"
                    className="text-xs font-medium border-slate-200 bg-slate-50"
                  >
                    <Bed className="w-3.5 h-3.5 mr-1 shrink-0" />
                    {cameraLabel}
                  </Badge>
                ) : null}
                <Badge className="bg-purple-100 text-purple-800 text-xs border-0 font-medium">
                  Fascicolo
                </Badge>
              </div>
            </div>

            <Button
              type="button"
              variant="destructive"
              disabled={pdf118Generating}
              onClick={handlePdf118Emergenza}
              className={`shrink-0 min-h-[48px] px-3 sm:px-4 touch-manipulation font-bold ${isSanitario ? "bg-red-600 hover:bg-red-700 shadow-sm" : "oss-emergency rounded-[0.875rem] sm:px-5"}`}
              title="Stampa PDF emergenza 118"
            >
              {pdf118Generating ? (
                <Loader2 className="w-5 h-5 animate-spin shrink-0" aria-hidden />
              ) : (
                <Ambulance className="w-5 h-5 shrink-0" aria-hidden />
              )}
              <span className="ml-1.5 hidden sm:inline">118</span>
              {!isSanitario ? <span className="ml-1 hidden md:inline text-xs font-semibold tracking-wide opacity-90">Emergenza</span> : null}
            </Button>
          </div>

          <div className="border-t border-slate-100 bg-slate-50/90 px-2 py-2 sm:px-3">
            <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6 gap-1.5 sm:gap-2 h-auto bg-transparent p-0 rounded-none border-0 shadow-none">
              <TabsTrigger value="pressione" className={tabTriggerClass} title="Pressione arteriosa">
                <Activity className="w-4 h-4 sm:w-5 sm:h-5 shrink-0 text-rose-500 group-data-[state=active]:text-white" />
                <span>Pressione</span>
              </TabsTrigger>
              <TabsTrigger value="glicemia" className={tabTriggerClass} title="Glicemia">
                <Droplets className="w-4 h-4 sm:w-5 sm:h-5 shrink-0 text-amber-500 group-data-[state=active]:text-white" />
                <span>Glicemia</span>
              </TabsTrigger>
              <TabsTrigger value="ossigenazione" className={tabTriggerClass} title="Ossigenazione">
                <HeartPulse className="w-4 h-4 sm:w-5 sm:h-5 shrink-0 text-sky-500 group-data-[state=active]:text-white" />
                <span>Ossigenazione</span>
              </TabsTrigger>
              <TabsTrigger value="diagnosi" className={tabTriggerClass} title="Diagnosi e patologie">
                <Heart className="w-4 h-4 sm:w-5 sm:h-5 shrink-0 text-red-500 group-data-[state=active]:text-white" />
                <span>Diagnosi</span>
              </TabsTrigger>
              <TabsTrigger value="terapie" className={tabTriggerClass} title="Terapie e ausili">
                <Pill className="w-4 h-4 sm:w-5 sm:h-5 shrink-0 text-blue-500 group-data-[state=active]:text-white" />
                <span className="inline-flex items-center gap-1">
                  Terapie
                  {alertTerapieInCorso > 0 ? (
                    <span className="inline-flex items-center justify-center min-w-[1.15rem] h-[1.15rem] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold tabular-nums group-data-[state=active]:bg-white group-data-[state=active]:text-amber-600">
                      {alertTerapieInCorso}
                    </span>
                  ) : null}
                </span>
              </TabsTrigger>
              <TabsTrigger value="terapie-extra" className={tabTriggerClass} title="Terapie extra">
                <Bell className="w-4 h-4 sm:w-5 sm:h-5 shrink-0 text-rose-500 group-data-[state=active]:text-white" />
                <span className="inline-flex items-center gap-1">
                  Extra
                  {alertTerapieExtra > 0 ? (
                    <span className="inline-flex items-center justify-center min-w-[1.15rem] h-[1.15rem] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold tabular-nums group-data-[state=active]:bg-white group-data-[state=active]:text-red-600">
                      {alertTerapieExtra}
                    </span>
                  ) : null}
                </span>
              </TabsTrigger>
            </TabsList>
          </div>
        </div>

        <TabsContent value="pressione" className="mt-0 focus-visible:outline-none">
          <OssWithingsPressione fascicoloId={id} />
        </TabsContent>

        <TabsContent value="glicemia" className="mt-0 focus-visible:outline-none">
          <MonitoraggioVitale tipo="glicemia" fascicoloId={id} forOss />
        </TabsContent>

        <TabsContent value="ossigenazione" className="mt-0 focus-visible:outline-none">
          <MonitoraggioVitale tipo="ossigenazione" fascicoloId={id} forOss />
        </TabsContent>

        <TabsContent value="diagnosi" className="mt-0 focus-visible:outline-none">
        <Card className="card-base">
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Heart className="w-5 h-5 text-red-500" />
              Diagnosi e Patologie
            </CardTitle>
            {!editDiagnosi ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setDiagnosiEditKey((k) => k + 1);
                  setDiagnosiForm(buildDiagnosiListe(san));
                  setEditDiagnosi(true);
                }}
                className="shrink-0"
              >
                <Pencil className="w-4 h-4 mr-1" />
                Modifica
              </Button>
            ) : (
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditDiagnosi(false)}
                  disabled={savingDiagnosi}
                >
                  <X className="w-4 h-4 mr-1" />
                  Annulla
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveDiagnosi}
                  disabled={savingDiagnosi}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {savingDiagnosi ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-1" />
                  )}
                  Salva
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {!editDiagnosi ? (
              <>
                <ReadField label="Diagnosi principale" value={san.diagnosi_principale} />

                <div>
                  <Label className="text-slate-500 text-xs">Diagnosi secondarie</Label>
                  {(san.diagnosi_secondarie || []).some((d) => String(d ?? "").trim()) ? (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {(san.diagnosi_secondarie || []).filter((d) => String(d ?? "").trim()).map((d, idx) => (
                        <Badge key={idx} variant="outline">{d}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400">Nessuna</p>
                  )}
                </div>

                <div>
                  <Label className="text-slate-500 text-xs">Patologie croniche</Label>
                  {(san.patologie_croniche || []).some((p) => String(p ?? "").trim()) ? (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {(san.patologie_croniche || []).filter((p) => String(p ?? "").trim()).map((p, idx) => (
                        <Badge key={idx} className="bg-orange-100 text-orange-700">{p}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400">Nessuna</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                  <ReadField label="Medico curante" value={san.medico_curante} />
                  <ReadField label="ASL di riferimento" value={san.asl_riferimento} />
                  <ReadField label="Telefono medico" value={san.medico_curante_telefono} />
                  <ReadField label="Email medico" value={san.medico_curante_email_personale} />
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <Label className="text-slate-500 text-xs">Gruppo sanguigno</Label>
                    <p className="font-bold text-lg text-red-600">{san.gruppo_sanguigno || "N/D"}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-xs">Dieta</Label>
                    <p className="font-medium">{san.dieta || "Normale"}</p>
                  </div>
                </div>

                <div className="p-3 bg-red-50 rounded-xl">
                  <Label className="text-red-600 text-xs font-semibold flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    ALLERGIE
                  </Label>
                  {(san.allergie || []).some((a) => String(a ?? "").trim()) ? (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(san.allergie || []).filter((a) => String(a ?? "").trim()).map((a, idx) => (
                        <Badge key={idx} className="bg-red-500 text-white">{a}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 mt-2">Nessuna allergia nota</p>
                  )}
                </div>
              </>
            ) : (
              /* Edit mode – Diagnosi */
              diagnosiForm && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs">Diagnosi principale</Label>
                    <Input
                      key={`diagnosi-dp-${diagnosiEditKey}`}
                      ref={diagnosiPrincipaleRef}
                      className="mt-1"
                      defaultValue={san.diagnosi_principale || ""}
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Diagnosi secondarie</Label>
                    <div className="mt-1">
                      <TagListEditor
                        values={diagnosiForm.diagnosi_secondarie}
                        onChange={(v) => setDiagnosiForm((f) => ({ ...f, diagnosi_secondarie: v }))}
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Patologie croniche</Label>
                    <div className="mt-1">
                      <TagListEditor
                        values={diagnosiForm.patologie_croniche}
                        onChange={(v) => setDiagnosiForm((f) => ({ ...f, patologie_croniche: v }))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-4 border-t">
                    <div>
                      <Label className="text-xs">Medico curante</Label>
                      <Input
                        key={`diagnosi-mc-${diagnosiEditKey}`}
                        ref={medicoCuranteRef}
                        className="mt-1"
                        defaultValue={san.medico_curante || ""}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">ASL di riferimento</Label>
                      <Input
                        key={`diagnosi-asl-${diagnosiEditKey}`}
                        ref={aslRiferimentoRef}
                        className="mt-1"
                        defaultValue={san.asl_riferimento || ""}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Telefono medico</Label>
                      <Input
                        key={`diagnosi-tel-${diagnosiEditKey}`}
                        ref={medicoTelRef}
                        className="mt-1"
                        defaultValue={san.medico_curante_telefono || ""}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Email medico</Label>
                      <Input
                        key={`diagnosi-em-${diagnosiEditKey}`}
                        ref={medicoEmailRef}
                        className="mt-1"
                        type="email"
                        defaultValue={san.medico_curante_email_personale || ""}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Gruppo sanguigno</Label>
                      <Input
                        key={`diagnosi-gs-${diagnosiEditKey}`}
                        ref={gruppoSanguignoRef}
                        className="mt-1"
                        defaultValue={san.gruppo_sanguigno || ""}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Dieta</Label>
                      <Input
                        key={`diagnosi-dieta-${diagnosiEditKey}`}
                        ref={dietaRef}
                        className="mt-1"
                        defaultValue={san.dieta || ""}
                      />
                    </div>
                  </div>

                  <div className="pt-2">
                    <Label className="text-xs text-red-600 font-semibold flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      ALLERGIE
                    </Label>
                    <div className="mt-1">
                      <TagListEditor
                        values={diagnosiForm.allergie}
                        onChange={(v) => setDiagnosiForm((f) => ({ ...f, allergie: v }))}
                      />
                    </div>
                  </div>
                </div>
              )
            )}
          </CardContent>
        </Card>
        </TabsContent>

        <TabsContent value="terapie" className="mt-0 focus-visible:outline-none">
        <div className="mb-6">
          <TerapieInCorsoSection
            fascicoloId={id}
            terapieInCorso={san.terapie_in_corso || []}
            ospiteInfo={{
              nome: anag.nome,
              cognome: anag.cognome,
              data_nascita: anag.data_nascita,
              sesso: anag.sesso,
              codice_fiscale: anag.codice_fiscale,
              camera: fascicolo.camera && fascicolo.camera !== "N/A" ? fascicolo.camera : "",
              numero_cartella: fascicolo.amministrativa?.numero_cartella,
            }}
            mode="oss"
            authHeaders={{ Authorization: `Bearer ${token}` }}
            onRefresh={() => {
              loadFascicolo();
              loadAlertCounts();
            }}
            onAlertsChange={setAlertTerapieInCorso}
          />
        </div>
        <Card className="card-base">
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Activity className="w-5 h-5 text-blue-500" />
              Ausili
            </CardTitle>
            {!editTerapie ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setTerapieEditKey((k) => k + 1);
                  setTerapieForm(buildAusiliListe(san));
                  setEditTerapie(true);
                }}
                className="shrink-0"
              >
                <Pencil className="w-4 h-4 mr-1" />
                Modifica
              </Button>
            ) : (
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditTerapie(false)}
                  disabled={savingTerapie}
                >
                  <X className="w-4 h-4 mr-1" />
                  Annulla
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveTerapie}
                  disabled={savingTerapie}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {savingTerapie ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-1" />
                  )}
                  Salva
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {!editTerapie ? (
              <>
                <div>
                  <Label className="text-slate-500 text-xs">Ausili utilizzati</Label>
                  {(san.ausili_utilizzati || []).some((a) => String(a ?? "").trim()) ? (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(san.ausili_utilizzati || []).filter((a) => String(a ?? "").trim()).map((a, idx) => (
                        <Badge key={idx} variant="outline" className="py-2">{a}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400 mt-2">Nessun ausilio</p>
                  )}
                </div>
              </>
            ) : (
              Array.isArray(terapieForm) && (
                <TagListEditor
                  values={terapieForm}
                  onChange={setTerapieForm}
                  placeholder="Es. Deambulatore — premi Invio o Aggiungi"
                />
              )
            )}
          </CardContent>
        </Card>

        <Card className="card-base mt-6">
          <CardContent className="space-y-4 pt-6">
            {!editTerapie ? (
              <>
                <div>
                  <Label className="text-slate-700 text-sm font-medium flex items-center gap-2 mb-2">
                    <Paperclip className="w-4 h-4 text-blue-500" />
                    Documenti Terapie e Ausili
                  </Label>
                  <p className="text-xs text-slate-500 mb-2">Allegati relativi a terapie o ausili</p>
                  {docsTerapieAusili.length > 0 ? (
                    <div className="space-y-2">
                      {docsTerapieAusili.map((doc) => (
                        <div
                          key={doc.file_id}
                          className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg border hover:bg-slate-100 transition-colors"
                        >
                          <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                            {[".jpg", ".jpeg", ".png", ".gif"].includes(doc.file_ext) ? (
                              <Image className="w-4 h-4 text-blue-600" />
                            ) : (
                              <File className="w-4 h-4 text-blue-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 text-sm truncate">{doc.file_name}</p>
                            <p className="text-xs text-slate-500">
                              {(doc.file_size / 1024).toFixed(1)} KB
                              {doc.uploaded_by_oss_id ? (
                                <span className="text-purple-600 ml-1">· Caricato da OSS</span>
                              ) : null}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleViewDocument(doc)}
                              className="text-blue-600 hover:bg-blue-100"
                              title="Visualizza"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownloadDocument(doc)}
                              className="text-emerald-600 hover:bg-emerald-100"
                              title="Scarica"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {canDeleteDoc(doc) ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteDocument(doc)}
                                className="text-red-600 hover:bg-red-100"
                                title="Elimina"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  <input
                    ref={terapieAusiliFileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx"
                    className="hidden"
                    onChange={handleUploadTerapieAusili}
                    id="oss-terapie-ausili-upload"
                  />
                  <label
                    htmlFor="oss-terapie-ausili-upload"
                    className={`inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium border border-blue-300 bg-transparent text-blue-600 hover:bg-blue-50 h-9 px-4 mt-2 cursor-pointer ${uploadingTerapieAusili ? "opacity-50 pointer-events-none" : ""}`}
                  >
                    {uploadingTerapieAusili ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    Carica file o foto
                  </label>
                </div>

                <div className="pt-4 border-t">
                  <Label className="text-slate-500 text-xs">Piani terapeutici (testo)</Label>
                  <p className="text-slate-700 mt-2 whitespace-pre-wrap">
                    {san.piani_terapeutici_testo || "Nessun testo inserito"}
                  </p>
                </div>

                <div className="pt-4 border-t">
                  <Label className="text-slate-700 text-sm font-medium flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-blue-500" />
                    Allegati Piani terapeutici
                  </Label>
                  {docsPianiTerapeutici.length > 0 ? (
                    <div className="space-y-2">
                      {docsPianiTerapeutici.map((doc) => {
                        const giorni = giorniAllaScadenzaDoc(doc.scadenza);
                        const scadenzaLabel = doc.scadenza
                          ? new Date(doc.scadenza).toLocaleDateString("it-IT")
                          : null;
                        return (
                          <div
                            key={doc.file_id}
                            className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg border hover:bg-slate-100 transition-colors"
                          >
                            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                              {[".jpg", ".jpeg", ".png", ".gif"].includes(doc.file_ext) ? (
                                <Image className="w-4 h-4 text-blue-600" />
                              ) : (
                                <File className="w-4 h-4 text-blue-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 text-sm truncate">{doc.file_name}</p>
                              <p className="text-xs text-slate-500">{(doc.file_size / 1024).toFixed(1)} KB</p>
                              {doc.scadenza ? (
                                <div className="text-xs mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                                  <span className="text-slate-500">Scadenza: {scadenzaLabel}</span>
                                  {giorni !== null && giorni < 0 && (
                                    <Badge variant="destructive" className="text-[10px]">Scaduto</Badge>
                                  )}
                                  {giorni !== null && giorni >= 0 && giorni <= 30 && (
                                    <Badge className="bg-amber-500 text-[10px]">Tra {giorni} gg</Badge>
                                  )}
                                </div>
                              ) : null}
                              {doc.uploaded_by_oss_id ? (
                                <p className="text-xs text-purple-600 mt-0.5">Caricato da OSS</p>
                              ) : null}
                            </div>
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleViewDocument(doc)}
                                className="text-blue-600 hover:bg-blue-100"
                                title="Visualizza"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDownloadDocument(doc)}
                                className="text-emerald-600 hover:bg-emerald-100"
                                title="Scarica"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              {canDeleteDoc(doc) ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteDocument(doc)}
                                  className="text-red-600 hover:bg-red-100"
                                  title="Elimina"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="oss-scadenza-piani" className="text-slate-600 text-xs whitespace-nowrap">
                        Data di scadenza
                      </Label>
                      <input
                        id="oss-scadenza-piani"
                        ref={scadenzaPianiRef}
                        type="date"
                        className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                        defaultValue=""
                      />
                    </div>
                    <input
                      ref={pianiTerapeuticiFileInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx"
                      className="hidden"
                      onChange={handleUploadPianoTerapeutico}
                      id="oss-piani-terapeutici-upload"
                    />
                    <label
                      htmlFor="oss-piani-terapeutici-upload"
                      className={`inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium border border-blue-300 bg-transparent text-blue-600 hover:bg-blue-50 h-9 px-4 cursor-pointer ${uploadingPianiTerapeutici ? "opacity-50 pointer-events-none" : ""}`}
                    >
                      {uploadingPianiTerapeutici ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      Carica file o foto
                    </label>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <Label className="text-slate-500 text-xs">Note sanitarie</Label>
                  <p className="text-slate-700 mt-2">{san.note_sanitarie || "Nessuna nota"}</p>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                  <div>
                    <Label className="text-slate-700 text-sm font-medium flex items-center gap-2 mb-2">
                      <Paperclip className="w-4 h-4 text-blue-500" />
                      Documenti Terapie e Ausili
                    </Label>
                    <p className="text-xs text-slate-500 mb-2">Allegati relativi a terapie o ausili</p>
                    {docsTerapieAusili.length > 0 ? (
                      <div className="space-y-2">
                        {docsTerapieAusili.map((doc) => (
                          <div
                            key={doc.file_id}
                            className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg border hover:bg-slate-100 transition-colors"
                          >
                            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                              {[".jpg", ".jpeg", ".png", ".gif"].includes(doc.file_ext) ? (
                                <Image className="w-4 h-4 text-blue-600" />
                              ) : (
                                <File className="w-4 h-4 text-blue-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 text-sm truncate">{doc.file_name}</p>
                              <p className="text-xs text-slate-500">
                                {(doc.file_size / 1024).toFixed(1)} KB
                                {doc.uploaded_by_oss_id ? (
                                  <span className="text-purple-600 ml-1">· Caricato da OSS</span>
                                ) : null}
                              </p>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleViewDocument(doc)}
                                className="text-blue-600 hover:bg-blue-100"
                                title="Visualizza"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDownloadDocument(doc)}
                                className="text-emerald-600 hover:bg-emerald-100"
                                title="Scarica"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              {canDeleteDoc(doc) ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteDocument(doc)}
                                  className="text-red-600 hover:bg-red-100"
                                  title="Elimina"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <input
                      ref={terapieAusiliFileInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx"
                      className="hidden"
                      onChange={handleUploadTerapieAusili}
                      id="oss-terapie-ausili-upload-edit"
                    />
                    <label
                      htmlFor="oss-terapie-ausili-upload-edit"
                      className={`inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium border border-blue-300 bg-transparent text-blue-600 hover:bg-blue-50 h-9 px-4 mt-2 cursor-pointer ${uploadingTerapieAusili ? "opacity-50 pointer-events-none" : ""}`}
                    >
                      {uploadingTerapieAusili ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      Carica file o foto
                    </label>
                  </div>

                  <div className="pt-4 border-t">
                    <Label className="text-xs">Piani terapeutici (testo)</Label>
                    <Textarea
                      key={`terapie-pt-${terapieEditKey}`}
                      ref={pianiTerapeuticiRef}
                      className="mt-1"
                      rows={4}
                      defaultValue={san.piani_terapeutici_testo || ""}
                    />
                  </div>

                  <div className="pt-4 border-t">
                    <Label className="text-slate-700 text-sm font-medium flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-blue-500" />
                      Allegati Piani terapeutici
                    </Label>
                    {docsPianiTerapeutici.length > 0 ? (
                      <div className="space-y-2">
                        {docsPianiTerapeutici.map((doc) => {
                          const giorni = giorniAllaScadenzaDoc(doc.scadenza);
                          const scadenzaLabel = doc.scadenza
                            ? new Date(doc.scadenza).toLocaleDateString("it-IT")
                            : null;
                          return (
                            <div
                              key={doc.file_id}
                              className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg border hover:bg-slate-100 transition-colors"
                            >
                              <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                {[".jpg", ".jpeg", ".png", ".gif"].includes(doc.file_ext) ? (
                                  <Image className="w-4 h-4 text-blue-600" />
                                ) : (
                                  <File className="w-4 h-4 text-blue-600" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-900 text-sm truncate">{doc.file_name}</p>
                                <p className="text-xs text-slate-500">{(doc.file_size / 1024).toFixed(1)} KB</p>
                                {doc.scadenza ? (
                                  <div className="text-xs mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                                    <span className="text-slate-500">Scadenza: {scadenzaLabel}</span>
                                    {giorni !== null && giorni < 0 && (
                                      <Badge variant="destructive" className="text-[10px]">Scaduto</Badge>
                                    )}
                                    {giorni !== null && giorni >= 0 && giorni <= 30 && (
                                      <Badge className="bg-amber-500 text-[10px]">Tra {giorni} gg</Badge>
                                    )}
                                  </div>
                                ) : null}
                                {doc.uploaded_by_oss_id ? (
                                  <p className="text-xs text-purple-600 mt-0.5">Caricato da OSS</p>
                                ) : null}
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleViewDocument(doc)}
                                  className="text-blue-600 hover:bg-blue-100"
                                  title="Visualizza"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDownloadDocument(doc)}
                                  className="text-emerald-600 hover:bg-emerald-100"
                                  title="Scarica"
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                {canDeleteDoc(doc) ? (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleDeleteDocument(doc)}
                                    className="text-red-600 hover:bg-red-100"
                                    title="Elimina"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="oss-scadenza-piani-edit" className="text-slate-600 text-xs whitespace-nowrap">
                          Data di scadenza
                        </Label>
                        <input
                          id="oss-scadenza-piani-edit"
                          ref={scadenzaPianiRef}
                          type="date"
                          className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                          defaultValue=""
                        />
                      </div>
                      <input
                        ref={pianiTerapeuticiFileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx"
                        className="hidden"
                        onChange={handleUploadPianoTerapeutico}
                        id="oss-piani-terapeutici-upload-edit"
                      />
                      <label
                        htmlFor="oss-piani-terapeutici-upload-edit"
                        className={`inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium border border-blue-300 bg-transparent text-blue-600 hover:bg-blue-50 h-9 px-4 cursor-pointer ${uploadingPianiTerapeutici ? "opacity-50 pointer-events-none" : ""}`}
                      >
                        {uploadingPianiTerapeutici ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                        Carica file o foto
                      </label>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Note sanitarie</Label>
                    <Textarea
                      key={`terapie-ns-${terapieEditKey}`}
                      ref={noteSanitarieRef}
                      className="mt-1"
                      rows={3}
                      defaultValue={san.note_sanitarie || ""}
                    />
                  </div>
                </div>
            )}
          </CardContent>
        </Card>
        </TabsContent>

        <TabsContent value="terapie-extra" className="mt-0 focus-visible:outline-none">
          <TerapieExtraSection
            fascicoloId={id}
            terapieExtra={san.terapie_extra || []}
            mode="oss"
            authHeaders={{ Authorization: `Bearer ${token}` }}
            onRefresh={() => {
              loadFascicolo();
              loadAlertCounts();
            }}
            onAlertsChange={setAlertTerapieExtra}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
